import BaseModel from "lib/model";
import Transactions from "collections/transactions";
import ShippingCharges from "collections/shipping-charges";
import ShippingInfoEntries from "collections/shipping-info";
import Orders from "collections/orders";
import assign from "lodash/assign";
import moment from "lib/moment-i18n";
export default BaseModel.extend({
    idAttribute: "_id",
    props: {
        _id: {
            type: "string",
            //required: true
        },
        brand: {
            type: "string",
            //required: true
        },
        order_group_id: {
            type: "string",
            //required: true
        },
        payment_group_number: {
            type: "number",
        },
        subtotal: {
            type: "number",
            //required: true,
            default: 0,
        },
        total_balance: {
            type: "number",
            //required: true,
            default: 0,
        },
        balance_pending: {
            type: "number",
            //required: true,
            default: 0,
        },
        balance_paid: {
            type: "number",
            //required: true,
            default: 0,
        },
        invoice_total: {
            type: "number",
            //required: true
        },
        currency_code: {
            type: "string",
            //required: true,
            //default: 'USD'
        },
        description: {
            type: "string",
            required: false,
        },
        __company: {
            type: "object",
        },
        __buyer: {
            type: "object",
        },
        days_after_authorizing: {
            type: "number",
            default: 0,
        },
        invoice: {
            type: "object",
        },
    },

    collections: {
        transactions: Transactions,
        shipping_charges: ShippingCharges,
        shipping_information: ShippingInfoEntries,
        orders: Orders,
    },

    session: {
        amount_to_pay_today: {
            type: "number",
            default: 0,
        },
        workingOrder: {
            type: "object",
        },
        shipping_amount: {
            type: "number",
            required: true,
            default: 0,
        },
        transaction_type: {
            type: "string",
        },
    },
    round(val) {
        return Math.round(val * 100) / 100;
    },
    derived: {
        //this shoudn't use balance_pending because that field indicates unpaid
        //funds, not unverified payments. --DM 2016.08.01
        balance_committed_to_pay: {
            deps: ["balance_paid"],
            fn: function () {
                return this.round(this.balance_paid);
            },
        },

        running_subtotal: {
            deps: ["subtotal", "workingOrder", "previous_shipping_info"],
            cache: false,
            fn: function () {
                let subtotal = this.subtotal;

                // If its a current working order, we need to take the current value from it
                if (this.workingOrder && this.workingOrder.total) {
                    // If its an edit, we need to figure out the difference between the old order and the new one
                    const oldOrder = this.orders.get ? this.orders.get(this.workingOrder.original_order_id) : undefined;
                    if (oldOrder) {
                        subtotal = this.subtotal + Number(this.workingOrder.total) - oldOrder.total;
                    } else {
                        subtotal = Number(this.workingOrder.total);
                    }
                }
                subtotal = subtotal - this.previous_shipping_info; // shipping_information totals are included in order totals already

                return this.round(subtotal);
            },
        },

        running_total: {
            // order amounts plus shipping amounts
            deps: [
                "subtotal",
                "workingOrder",
                "shipping_amount",
                "previous_shipping_charges",
                "previous_shipping_info",
            ],
            cache: false,
            fn: function () {
                return this.round(
                    this.running_subtotal +
                        this.shipping_amount +
                        this.previous_shipping_charges +
                        this.previous_shipping_info
                );
            },
        },

        unpaid_balance: {
            deps: ["total", "balance_committed_to_pay", "amount_to_pay_today"],
            cache: false,
            fn: function () {
                const derivedBalanceRemaining = this.running_total - this.balance_committed_to_pay;
                return this.round(derivedBalanceRemaining);
            },
        },

        balance_remaining: {
            deps: ["total", "balance_committed_to_pay", "amount_to_pay_today"],
            cache: false,
            fn: function () {
                let derivedBalanceRemaining = this.running_total - this.balance_committed_to_pay;

                if (this.amount_to_pay_today && this.amount_to_pay_today > 0) {
                    derivedBalanceRemaining = derivedBalanceRemaining - this.amount_to_pay_today;
                }

                return this.round(derivedBalanceRemaining);
            },
        },
        balance_authorized: {
            deps: ["transactions", "days_after_authorizing"],
            cache: false,
            fn: function () {
                let sum = 0;
                if (!this.transactions || !this.transactions.length) {
                    return sum;
                }

                this.transactions.forEach((transaction) => {
                    const isAuthTxInValidState =
                        transaction.type === "authorize" &&
                        !transaction.capture &&
                        !["declined", "fraud", "expired", "failed", "cancelled"].includes(transaction.status);
                    this.days_after_authorizing = this.days_after_authorizing || 0;
                    const authValidUntilThisDate = moment
                        .utc(transaction.created_on)
                        .add(this.days_after_authorizing, "day");
                    authValidUntilThisDate.isAfter(moment.utc());
                    const isAuthValid =
                        (this.days_after_authorizing === 0 || authValidUntilThisDate.isAfter(moment.utc())) &&
                        isAuthTxInValidState;

                    if (isAuthValid) {
                        sum += transaction.amount;
                    }
                });
                return sum;
            },
        },
        balance_remaining_less_authorized: {
            deps: ["total", "balance_committed_to_pay", "amount_to_pay_today", "balance_authorized"],
            cache: false,
            fn: function () {
                let derivedBalanceRemaining =
                    this.running_total - this.balance_committed_to_pay - (this.balance_authorized || 0);

                if (this.amount_to_pay_today && this.amount_to_pay_today > 0) {
                    derivedBalanceRemaining = derivedBalanceRemaining - this.amount_to_pay_today;
                }

                return this.round(derivedBalanceRemaining);
            },
        },

        previous_shipping_info: {
            deps: ["shipping_information"],
            cache: false,
            fn: function () {
                let charges = 0;

                this.shipping_information.forEach(function (shippingCharge) {
                    charges += shippingCharge.final_amount;
                });

                return this.round(charges);
            },
        },

        previous_shipping_charges: {
            deps: ["shipping_charges"],
            cache: false,
            fn: function () {
                let charges = 0;
                this.shipping_charges.forEach(function (charge) {
                    charges += charge.amount || 0;
                });

                return this.round(charges);
            },
        },

        all_shipping_fees: {
            deps: ["shipping_charges", "shipping_information"],
            cache: false,
            fn: function () {
                let charges = 0;
                this.shipping_charges.forEach(function (charge) {
                    charges += charge.amount || 0;
                });

                this.shipping_information.forEach(function (shippingCharge) {
                    charges += shippingCharge.final_amount;
                });

                return this.round(charges);
            },
        },
    },
    defaultShowShippingCharges: function () {
        return this.get("shipping_charges").length > 0;
    },

    // Left in for legacy use
    formatCurrency: function (field) {
        return "$" + this.get(field).toFixed(2);
    },

    onlyHasCancelledOrders: function () {
        return !this.orders.some((order) => {
            return order.__status !== "cancelled";
        });
    },

    validateFieldForNewModel: function (key, value) {
        let error;
        switch (key) {
            case "amount_to_pay_today":
                if (value < 2) {
                    error = "Amount must be greater than $2.00";
                } else if (this.round(this.running_total - this.balance_committed_to_pay - value) < 0) {
                    error = "Amount to pay must be less than or equal to total";
                }
                // still force set to show the other updated fields to the user
                this.amount_to_pay_today = value;
                break;
            case "shipping_amount":
                if (value < 0) {
                    error = "Amount must be greater than 0";
                }
                this.shipping_amount = value;
                break;
            default:
                error = false;
        }

        return error;
    },
    _validate: function (attrs, options) {
        if (!options.validate || !this.validate) {
            return true;
        }
        if (!options.validateJustCurrentAttrs) {
            attrs = assign({}, this.attributes, attrs);
        }
        const error = (this.validationError = this.validate(attrs, options) || null);

        if (!error) {
            return true;
        }
        this.trigger("invalid", this, error, assign(options || {}, { validationError: error }));
        return false;
    },
    validate: function (attrs) {
        const errors = {};

        Object.keys(attrs).forEach((key) => {
            const error = this.validateFieldForNewModel(key, attrs[key]);
            if (error) {
                errors[key] = error;
            }
        });

        return Object.keys(errors).length ? errors : false;
    },

    fetch: function (options) {
        if (options && options.paymentGroup) {
            this.url = `/payment-group/group/${options.paymentGroup}`;
        }
        BaseModel.prototype.fetch.call(this, options);
    },

    url: function () {
        if (this.isNew()) {
            return `/payment-group/group/${this.order_group_id}`;
        } else {
            return `/payment-group/${this._id}`;
        }
    },
});
